<script setup lang="ts">
import WrIcon from "../global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface TechItem {
  title: string;
  description: string;
  svgIcon?: string;
}

withDefaults(
  defineProps<{
    heading: string;
    subtitle: string;
    items: TechItem[];
    eyebrow?: string;
  }>(),
  {
    eyebrow: "Tooling",
  },
);
</script>

<template>
  <section class="relative">
    <div class="text-center">
      <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />{{ eyebrow }}</span>
      <h2 class="fl-heading">{{ heading }}</h2>
      <p class="fl-subcopy" :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'">{{ subtitle }}</p>
    </div>

    <div class="mt-10 lg:mt-14 px-4 max-w-[880px] mx-auto">
      <div class="fl-stack">
        <div v-for="item in items" :key="item.title" class="fl-item">
            <span class="fl-ic">
              <WrIcon :name="item.svgIcon || 'streamline:programming-script-1-language-programming-code'" class="size-5 text-[#12DBFF]" />
            </span>
            <div class="fl-item-body">
              <h3 class="fl-item-title">{{ item.title }}</h3>
              <p class="fl-item-desc">{{ item.description }}</p>
            </div>
          </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #12dbff; box-shadow: 0 0 8px rgba(18, 219, 255, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(28px, 4vw, 48px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

/* ── Two-column list ── */
.fl-stack { display: grid; grid-template-columns: 1fr; }
@media (min-width: 1024px) {
  .fl-stack { grid-template-columns: 1fr 1fr; column-gap: 56px; }
}

.fl-item {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 18px 0;
  border-bottom: 1px solid rgba(136, 127, 255, 0.15);
}
@media (min-width: 1024px) { .fl-item { gap: 16px; padding: 20px 0; } }

.fl-ic {
  width: 40px; height: 40px; flex-shrink: 0; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(18, 219, 255, 0.25); background: rgba(18, 219, 255, 0.08);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}
.fl-item:hover .fl-ic {
  border-color: rgba(18, 219, 255, 0.55);
  box-shadow: 0 0 14px rgba(18, 219, 255, 0.25);
}

.fl-item-body { min-width: 0; }
.fl-item-title { font-size: 15px; font-weight: 600; color: #fff; }
.fl-item-desc { font-size: 13px; line-height: 1.6; color: #bcb2c3; margin-top: 4px; }
@media (min-width: 1024px) {
  .fl-item-title { font-size: 16px; }
  .fl-item-desc { font-size: 14px; }
}

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-item { border-bottom-color: rgba(208, 208, 208, 0.5); }
html:not(.my-app-dark) .fl-ic {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  border-color: transparent;
}
html:not(.my-app-dark) .fl-item:hover .fl-ic {
  border-color: rgba(18, 219, 255, 0.5);
  box-shadow: 0 0 12px rgba(18, 219, 255, 0.2);
}
html:not(.my-app-dark) .fl-item-title { color: #613faa; }
html:not(.my-app-dark) .fl-item-desc { color: #444; }
</style>
